import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../StyledComponents";
import { appointmentData } from "../Utils/SectionsDataMap";
import FollowUp from "./FollowUp";
import Referral from "./Referral";
import IPDAdmissionReferral from "./IPDAdmissionReferral";
import fieldSetsSchema from "../config/schema/doctorConsoleFieldSets.json";
import { buildFieldLayoutFromSet } from "../config/defaults/fieldLayoutUtils";

// The Admission fields ship disabled, so config-driven consoles hide the block until a
// manager enables them. Quick Consultation renders this component without any config,
// so it falls back to the full field list and keeps behaving exactly as before.
const ADMISSION_FIELD_SET = fieldSetsSchema.fieldSets.ipdAdmissionFields;
const LEGACY_ADMISSION_LAYOUT = buildFieldLayoutFromSet({
  ...ADMISSION_FIELD_SET,
  fields: (ADMISSION_FIELD_SET.fields || []).map((field) => ({
    ...field,
    enabled: true,
  })),
});

export default function FollowUpReferralComp({
  sectionName,
  activeSection,
  followUpData,
  referralData,
  setFollowUpData,
  setReferralData,
  setFollowUpError,
  setReferralError,
  followUpError,
  referralError,
  dateForAppointment,
  ipdReferralData = [],
  setIpdReferralData,
  setIpdReferralError,
  ipdReferralError = {},
  subFieldLayouts,
}) {
  const admissionFieldLayout = subFieldLayouts?.admission ?? LEGACY_ADMISSION_LAYOUT;
  const showIpdAdmission = admissionFieldLayout.fields.length > 0;
  useEffect(() => {
    if (followUpData.length > 0) {
      setFollowUpDetails(followUpData[0]);
    } else {
      setFollowUpDetails({
        start_date: null,
        service_type: "",
        duration: "",
      });
    }
  }, [followUpData]);

  useEffect(() => {
    if (referralData.length > 0) {
      setReferralDetails(referralData[0]);
    } else {
      setReferralDetails({
        service_category: null,
        service_description: "",
        doctor_details: null,
      });
    }
  }, [referralData]);

  const [followUpDetails, setFollowUpDetails] = useState(
    followUpData.length > 0
      ? followUpData[0]
      : {
        start_date: null,
        service_type: "",
        duration: "",
      }
  );

  const [referralDetails, setReferralDetails] = useState(
    referralData.length > 0
      ? referralData[0]
      : {
        service_category: null,
        service_description: "",
        doctor_details: null,
      }
  );

  const [ipdDetails, setIpdDetails] = useState(
    ipdReferralData && ipdReferralData.length > 0
      ? ipdReferralData[0]
      : {
          admission_details: "",
          admission_type: "",
          expected_date: null,
          service_category: null,
          doctor_details: null,
        }
  );

  // Sync from parent by value, not by array identity. Parents often pass
  // `ipdReferralData || []`, which creates a new [] every render and was
  // wiping in-progress typing via the empty-branch reset below.
  const ipdReferralDataKey = JSON.stringify(ipdReferralData ?? []);
  useEffect(() => {
    if (ipdReferralData && ipdReferralData.length > 0) {
      setIpdDetails(ipdReferralData[0]);
    } else {
      setIpdDetails({
        admission_details: "",
        admission_type: "",
        expected_date: null,
        service_category: null,
        doctor_details: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by serialized value
  }, [ipdReferralDataKey]);
  // console.log("followupData", followUpData);
  // console.log("followupDetails", followUpDetails);
  // console.log("referralDetails", referralDetails);
  return (
    <ThemeProvider theme={theme}>
      {activeSection ? (
        <div>
          <FollowUp
            sectionName={sectionName}
            activeSection={activeSection}
            data={followUpData}
            setData={setFollowUpData}
            setFollowUpError={setFollowUpError}
            followUpError={followUpError}
            details={followUpDetails}
            setDetails={setFollowUpDetails}
            dateForAppointment={dateForAppointment}
            fieldLayout={subFieldLayouts?.followUp}
          />

          <Referral
            sectionName={sectionName}
            activeSection={activeSection}
            data={referralData}
            setData={setReferralData}
            setAppointmentError={setReferralError}
            appointmentError={referralError}
            details={referralDetails}
            setDetails={setReferralDetails}
            fieldLayout={subFieldLayouts?.referral}
          />

          {showIpdAdmission && (
            <IPDAdmissionReferral
              sectionName={sectionName}
              activeSection={activeSection}
              data={ipdReferralData}
              setData={setIpdReferralData}
              setAppointmentError={setIpdReferralError}
              appointmentError={ipdReferralError}
              details={ipdDetails}
              setDetails={setIpdDetails}
              fieldLayout={admissionFieldLayout}
            />
          )}
        </div>
      ) : (
        appointmentData(
          [{ ...followUpDetails }],
          [{ ...referralDetails }],
          followUpError,
          referralError,
          showIpdAdmission ? [{ ...ipdDetails }] : [],
          showIpdAdmission ? ipdReferralError : {}
        )
        // []
      )}
    </ThemeProvider>
  );
}
